import { COPILOT_BAR_ANSWER_DISPLAY_MODES, COPILOT_BAR_LOADING_PHRASE_PRIORITY } from './constants';

/**
 * @param {Object|null|undefined} answerDisplay - chatSetup.answerDisplay config
 * @returns {Object} Partial copilotBarProps to merge on stream start
 */
export const getStreamStartAnswerDisplayProps = (answerDisplay) => {
    if (!answerDisplay?.initialMode) {
        return {};
    }

    return {
        answerDisplayMode: answerDisplay.initialMode,
    };
};

/**
 * @param {Object} params
 * @param {string|null|undefined} params.mode - Current COPILOT_BAR_ANSWER_DISPLAY_MODES value
 * @param {string} params.text - Latest streamed answer text
 * @param {boolean} [params.dismissible=true]
 * @param {string} [params.loadingPhrasePriority]
 * @param {boolean} [params.formProgressLocksLoadingPhrase=false]
 * @returns {Object} Partial copilotBarProps to merge (empty if no update)
 */
export const resolveAnswerBarUpdate = ({
    mode,
    text,
    dismissible = true,
    loadingPhrasePriority = COPILOT_BAR_LOADING_PHRASE_PRIORITY.STREAM_TEXT,
    formProgressLocksLoadingPhrase = false,
}) => {
    if (!text || !mode) {
        return {};
    }

    if (mode === COPILOT_BAR_ANSWER_DISPLAY_MODES.LOADING) {
        const shouldUpdateLoadingPhrase =
            loadingPhrasePriority === COPILOT_BAR_LOADING_PHRASE_PRIORITY.STREAM_TEXT ||
            !formProgressLocksLoadingPhrase;

        if (!shouldUpdateLoadingPhrase) {
            return {};
        }

        return { loadingPhrase: text };
    }

    if (mode === COPILOT_BAR_ANSWER_DISPLAY_MODES.FEEDBACK) {
        return {
            feedback: {
                dismissible,
                isStreaming: true,
                message: text,
            },
        };
    }

    return {};
};

/**
 * @param {Object} params
 * @param {string} params.toMode - Target display mode after transition
 * @param {string} params.accumulatedText - Stream text accumulated before transition
 * @param {boolean} [params.dismissible=true]
 * @returns {Object} Partial copilotBarProps to merge
 */
export const resolveTransitionUpdate = ({ toMode, accumulatedText, dismissible = true }) => {
    if (toMode !== COPILOT_BAR_ANSWER_DISPLAY_MODES.FEEDBACK) {
        return { answerDisplayMode: toMode };
    }

    return {
        answerDisplayMode: toMode,
        loadingPhrase: '',
        ...(accumulatedText
            ? {
                  feedback: {
                      dismissible,
                      isStreaming: false,
                      message: accumulatedText,
                  },
              }
            : {}),
    };
};

/**
 * @param {Object} params
 * @param {string|null|undefined} params.mode
 * @param {string} params.text - Final answer text from stream finish
 * @param {boolean} [params.dismissible=true]
 * @returns {Object} Partial copilotBarProps to merge
 */
export const resolveFinishAnswerDisplayUpdate = ({ mode, text, dismissible = true }) => {
    if (!text || mode !== COPILOT_BAR_ANSWER_DISPLAY_MODES.FEEDBACK) {
        return {};
    }

    return {
        feedback: {
            dismissible,
            isStreaming: false,
            message: text,
        },
    };
};

/**
 * @param {Object|null|undefined} answerDisplay
 * @param {string} eventKey - e.g. 'onForm', 'onFinish'
 * @returns {string|null} Target mode after transition, or null if not configured
 */
export const getAnswerDisplayTransitionTarget = (answerDisplay, eventKey) => {
    return answerDisplay?.transitions?.[eventKey] ?? null;
};

/**
 * Extracts text from the last assistant message in a response.done output array.
 *
 * @param {Object|null|undefined} response - response.done payload
 * @returns {string} Combined text from the last assistant message, or empty string
 */
export const extractLastAssistantOutputText = (response) => {
    const output = response?.output;
    if (!Array.isArray(output) || output.length === 0) {
        return '';
    }

    for (let index = output.length - 1; index >= 0; index -= 1) {
        const item = output[index];
        if (item?.type !== 'message' || item?.role !== 'assistant') {
            continue;
        }

        const textParts = (item.content ?? [])
            .filter((part) => part?.type === 'text' && part.text)
            .map((part) => part.text);

        if (textParts.length > 0) {
            return textParts.join('\n\n');
        }
    }

    return '';
};

/**
 * @param {Object} params
 * @param {string} params.text - Final assistant message from response.done
 * @param {boolean} [params.dismissible=true]
 * @returns {Object} Partial copilotBarProps to merge (empty if no text)
 */
export const resolveResponseDoneAnswerDisplayUpdate = ({ text, dismissible = true }) => {
    if (!text) {
        return {};
    }

    return {
        answerDisplayMode: COPILOT_BAR_ANSWER_DISPLAY_MODES.FEEDBACK,
        loadingPhrase: '',
        feedback: {
            dismissible,
            isStreaming: false,
            message: text,
        },
    };
};
